// Tema del guest PWA. El CSS ya trae claro (:root), oscuro
// (@media prefers-color-scheme:dark) y los hooks de override :root.light /
// :root.dark. Aquí sólo gestionamos la ELECCIÓN del comensal:
//   'auto'  → sin clase → sigue al sistema operativo (comportamiento previo)
//   'light' → :root.light
//   'dark'  → :root.dark
// Persistido en localStorage para sobrevivir reload. Se aplica en main.tsx
// ANTES del render para evitar el flash de tema.
import { useEffect, useState } from 'react'

export type ThemeChoice = 'auto' | 'light' | 'dark'
const KEY = 'dhk8-guest-theme'

export function readTheme(): ThemeChoice {
  if (typeof window === 'undefined') return 'auto'
  const v = window.localStorage.getItem(KEY)
  return v === 'light' || v === 'dark' ? v : 'auto'
}

/** Aplica la elección al <html>: quita ambas clases y pone la explícita (o
 *  ninguna en 'auto'). Idempotente — seguro llamarlo en cada cambio. */
export function applyTheme(choice: ThemeChoice): void {
  if (typeof document === 'undefined') return
  const el = document.documentElement
  el.classList.remove('light', 'dark')
  if (choice !== 'auto') el.classList.add(choice)
}

export function setTheme(choice: ThemeChoice): void {
  if (typeof window !== 'undefined') window.localStorage.setItem(KEY, choice)
  applyTheme(choice)
  window.dispatchEvent(new CustomEvent('dhk8-theme', { detail: choice }))
}

/** ¿El sistema operativo pide oscuro? (resuelve 'auto'). */
export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
}

/** Tema EFECTIVO que ve el comensal: 'auto' se resuelve contra el SO. */
export function effectiveTheme(choice: ThemeChoice): 'light' | 'dark' {
  return choice === 'auto' ? (systemPrefersDark() ? 'dark' : 'light') : choice
}

/** Alterna claro↔oscuro EXPLÍCITO desde el tema efectivo (patrón sol/luna de
 *  POS/ADMIN): un tap fija la elección opuesta a lo que se ve ahora. */
export function toggleTheme(current: ThemeChoice): void {
  setTheme(effectiveTheme(current) === 'dark' ? 'light' : 'dark')
}

/** Hook reactivo para los controles de tema (se sincroniza entre componentes
 *  vía el evento 'dhk8-theme'). */
export function useTheme(): [ThemeChoice, (c: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(readTheme)
  const [, force] = useState(0)
  useEffect(() => {
    const on = (e: Event) => setChoice((e as CustomEvent<ThemeChoice>).detail)
    window.addEventListener('dhk8-theme', on)
    // En 'auto' el ícono sol/luna depende del SO — re-render si el SO cambia.
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    const onSys = (): void => force((n) => n + 1)
    mq?.addEventListener?.('change', onSys)
    return () => {
      window.removeEventListener('dhk8-theme', on)
      mq?.removeEventListener?.('change', onSys)
    }
  }, [])
  return [choice, setTheme]
}
